import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasHydrated: boolean;

  // Actions
  setAuth: (user: User, token: string) => void;
  setToken: (token: string) => void;
  updateUser: (user: Partial<User>) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  setHydrated: (hydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      hasHydrated: false,

      setAuth: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          hasHydrated: true,
        }),

      setToken: (token) =>
        set({
          token,
          isAuthenticated: true,
          isLoading: false,
          hasHydrated: true,
        }),

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      logout: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          hasHydrated: true,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

      setHydrated: (hasHydrated) => set({ hasHydrated }),
    }),
    {
      name: 'auth-storage',
      // token 与认证态都以内存和静默刷新结果为准，不持久化到 localStorage
      partialize: (state) => ({
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated(true);
        }
      },
      version: 2,
      migrate: (persistedState, _version) => {
        // 清除旧版本的 refreshToken 字段，避免残留在 localStorage
        if (!persistedState || typeof persistedState !== 'object') {
          console.warn('Invalid persisted state, resetting auth');
          return {
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: true,
            hasHydrated: true,
          } as AuthState;
        }
        const state = persistedState as AuthState;
        // 旧版本可能存了 refreshToken，强制清空
        if ('refreshToken' in (state as any)) {
          delete (state as any).refreshToken;
        }
        // token 与认证态不应持久化，恢复后必须重新静默刷新
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = true;
        state.hasHydrated = true;
        return state;
      },
    }
  )
);
